import { selectIsBossOrSalesBossLevel } from 'core/userContext';
import { getCampaignStateForTransition } from 'modules/campaigns/editor/utils';
import { CampaignState } from 'modules/domains/campaign';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { AbortedBadge } from 'ui/badge';
import { OverflowMenu } from 'ui/actionMenu';
import { StyledOptions } from 'ui/actionMenu/ActionMenu.styles';
import { ActionMenuOption, AllowedActionMenuElements } from 'ui/actionMenu/types';
import { BUTTON_SIZES } from 'ui/button';
import { Play, Stop } from 'ui/icons';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { OpenModalFn } from 'modules/campaigns/lib/hooks/useModals';
import {
  ModalProps,
  ModalType,
  useCampaignContext,
  useModalContext,
} from 'modules/campaigns/editor/context';

export const CAMPAIGN_EDITOR_BAUS_OVERFLOW_MENU_TEST_ID = 'campaign-editor-baus-overflow-menu';

const OverflowMenuItem = styled.div`
  gap: ${spacing.threeQuarter};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledOverflowMenu = styled(OverflowMenu)`
  ${StyledOptions} {
    width: ${pxToRem(250)};
  }
`;

const FORCE_RUNNING_ID = 'force-set-running';
const FORCE_FINISHED_ID = 'force-set-finished';

const BossBadge = () => <AbortedBadge>BOSS</AbortedBadge>;

/**
 * @param campaignState
 * @param openModal
 * @returns either `Force set running` or `Force set finished` option
 * depending on the current campaign state:
 *
 * A finished or aborted campaign state means the campaign can be force
 * set to running.
 *
 * A running campaign state means the campaign can be set to finished.
 */
const generateBausOptions = (
  campaignState: CampaignState,
  openModal: OpenModalFn<ModalType, ModalProps>,
): ActionMenuOption[] => {
  const eligibleNewState = getCampaignStateForTransition(campaignState);
  const canSetRunning = eligibleNewState === CampaignState.Running;

  return [
    {
      id: canSetRunning ? FORCE_RUNNING_ID : FORCE_FINISHED_ID,
      as: AllowedActionMenuElements.Button,
      content: (
        <OverflowMenuItem>
          {canSetRunning ? <Play height={18} width={18} /> : <Stop />}
          Force set {eligibleNewState?.toLowerCase()}
          <BossBadge />
        </OverflowMenuItem>
      ),
      onClick: () => {
        openModal(ModalType.ChangeCampaignState);
      },
    },
  ];
};

export const BausOverflowMenu = () => {
  const {
    campaign: { campaignState },
  } = useCampaignContext();
  const { openModal } = useModalContext();

  /**
   * We are guarding access to these boss controls with general boss access
   * which is granted to employees at large and encompasses all granular
   * boss permissions, including the one used for the original implementation
   * in Angular, `ViewBlastCampaignState`, as the granular permissions are not
   * exposed or used anywhere in Reactland currently.
   *
   * For more context: https://iterable.slack.com/archives/CFGSACSH3/p1684278417074639
   */
  const isBossOrSalesBossLevel = useSelector(selectIsBossOrSalesBossLevel);

  return isBossOrSalesBossLevel ? (
    <StyledOverflowMenu
      size={BUTTON_SIZES.LARGE}
      options={generateBausOptions(campaignState, openModal)}
      id={CAMPAIGN_EDITOR_BAUS_OVERFLOW_MENU_TEST_ID}
      data-test={CAMPAIGN_EDITOR_BAUS_OVERFLOW_MENU_TEST_ID}
    />
  ) : null;
};
